<script lang="ts" setup>
  const giftCardStore = useGiftCardsStore()
</script>

<template>
  <div class="step-list-container">
    <StepList class="step-list">
      <Step v-slot="{ value, activateCallback, a11yAttrs }" :value="1" as-child>
        <div
          :class="[
            'step-container',
            'divider',
            'first',
            { 'past': (value as number) < giftCardStore.wizard.activeStep },
            { 'active': value === giftCardStore.wizard.activeStep },
            { 'future': (value as number) > giftCardStore.wizard.activeStep }
          ]"
          v-bind="a11yAttrs.root"
        >
          <div class="step-content">
            <button v-bind="a11yAttrs.header" @click="activateCallback">
              <icon-ionic-pencil
                :color="
                  (value as number) < giftCardStore.wizard.activeStep
                    ? 'var(--color-base-black)'
                    : value === giftCardStore.wizard.activeStep
                      ? 'var(--color-base-white)'
                      : 'var(--color-text-muted)'
                "
                height="16"
                width="16"
              />
            </button>
            <span class="step-text body-small">Design</span>
          </div>

          <Divider
            :pt="{
              root: {
                class: '-mr-2'
              }
            }"
          />
        </div>
      </Step>

      <Step
        v-slot="{ value, activateCallback, a11yAttrs }"
        :disabled="!giftCardStore.wizard.values.skin?.id"
        :value="2"
        as-child
      >
        <div
          :class="[
            'step-container',
            'divider',
            { 'past': (value as number) < giftCardStore.wizard.activeStep },
            { 'active': value === giftCardStore.wizard.activeStep },
            { 'future': (value as number) > giftCardStore.wizard.activeStep }
          ]"
          v-bind="a11yAttrs.root"
        >
          <div class="step-content">
            <button
              :disabled="!giftCardStore.wizard.values.skin?.id"
              v-bind="a11yAttrs.header"
              @click="activateCallback"
            >
              <icon-ionic-gift
                :color="
                  (value as number) < giftCardStore.wizard.activeStep
                    ? 'var(--color-base-black)'
                    : value === giftCardStore.wizard.activeStep
                      ? 'var(--color-base-white)'
                      : 'var(--color-text-muted)'
                "
                height="16"
                width="16"
              />
            </button>
            <span class="step-text body-small">Personalise</span>
          </div>

          <Divider
            :pt="{
              root: {
                class: ['-ml-2', { '-mr-3': !giftCardStore.isForRecipient }]
              }
            }"
          />
        </div>
      </Step>

      <Step
        v-if="giftCardStore.isForRecipient"
        v-slot="{ a11yAttrs, value }"
        :disabled="giftCardStore.wizard.activeStep < 3"
        :value="3"
        as-child
      >
        <div
          :class="[
            'step-container',
            'divider',
            { 'past': (value as number) < giftCardStore.wizard.activeStep },
            { 'active': (value as number) === giftCardStore.wizard.activeStep },
            { 'future': (value as number) > giftCardStore.wizard.activeStep }
          ]"
          v-bind="a11yAttrs.root"
        >
          <div class="step-content">
            <button :disabled="giftCardStore.wizard.activeStep < (value as number)" v-bind="a11yAttrs.header">
              <icon-ionic-person
                :color="
                  (value as number) < giftCardStore.wizard.activeStep
                    ? 'var(--color-base-black)'
                    : value === giftCardStore.wizard.activeStep
                      ? 'var(--color-base-white)'
                      : 'var(--color-text-muted)'
                "
                height="16"
                width="16"
              />
            </button>
            <span class="step-text body-small">Recipient</span>
          </div>

          <Divider
            :pt="{
              root: {
                class: '-mr-3'
              }
            }"
          />
        </div>
      </Step>

      <Step v-slot="{ a11yAttrs, value }" :disabled="giftCardStore.wizard.activeStep !== 4" :value="4" as-child>
        <div
          :class="[
            'step-container',
            'last',
            { 'active': value === giftCardStore.wizard.activeStep },
            { 'future': (value as number) > giftCardStore.wizard.activeStep }
          ]"
          v-bind="a11yAttrs.root"
        >
          <div class="step-content">
            <button :disabled="value !== giftCardStore.wizard.activeStep" v-bind="a11yAttrs.header">
              <icon-ionic-card
                :color="
                  value === giftCardStore.wizard.activeStep ? 'var(--color-base-white)' : 'var(--color-text-muted)'
                "
                height="16"
                width="16"
              />
            </button>
            <span class="step-text body-small">Purchase</span>
          </div>
        </div>
      </Step>
    </StepList>
  </div>
</template>

<style lang="scss" scoped>
  .step-list-container {
    display: flex;
    justify-content: center; /* Center horizontally */
    align-items: center; /* Center vertically if needed */
    width: 100%; /* Ensure the parent takes full width */
    height: 100%; /* Ensure the parent takes full height if needed */

    .step-list {
      width: 629px;

      .step-container {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        margin-bottom: 24px;

        .step-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 48px; /* Set a fixed width */
          gap: 0.5rem;
        }

        button {
          aspect-ratio: 1;
          padding: 0.5rem;

          background-color: var(--color-neutral-grey-lightest);
          border-radius: 50%;
          border: 1px solid transparent;

          cursor: pointer;
        }

        button:disabled {
          cursor: not-allowed;
        }
      }

      .step-container.divider {
        flex: 1;
      }

      .step-container.past {
        button {
          background-color: var(--color-base-white);
          border-color: var(--color-stroke-light);
        }
      }

      .step-container.active {
        button {
          background-color: var(--color-base-red);
        }
      }

      .step-container.future {
        button {
          background-color: var(--color-neutral-grey-lightest);
          border-color: transparent;
        }

        .step-text {
          color: var(--color-text-muted);
          white-space: nowrap; /* Prevent text from wrapping */
          overflow: hidden; /* Hide overflow text */
        }
      }

      .step-container.first {
        .step-content {
          align-items: flex-start;
          width: 40px; /* Set a fixed width */
        }
      }

      .step-container.last {
        .step-content {
          align-items: flex-end;
          width: 40px; /* Set a fixed width */
        }
      }
    }
  }
</style>
